import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Segmented, Spin, ConfigProvider, Button } from "antd";
import { SettingOutlined } from "@ant-design/icons";

import { useAppData } from "./context/DataContext";
import Navbar from "./Navbar";
import Footer from "./Footer";
import EventCard from "./EventCard";
import { ReactComponent as Avatar } from "./IconUmmahfy.svg";
import { ReactComponent as NoEvents } from "./NoEvents.svg";
import { slugifyOrgName } from "./utils/slug";

const secrets = require("./secrets");
const api = secrets.server_url;

/**
 * Public, read-only organization profile page.
 * Route: /organization/:slug
 *
 * Anyone (logged in or not) can view this page when they click an
 * organization's name on an event page.
 *
 * If the viewer manages this organization (or is an admin), a "Manage"
 * button appears that links them to the existing organizer dashboard
 * at /organization.
 */
function OrganizationProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const {
    managedOrganizations,
    selectedOrganization,
    setAppData,
  } = useAppData();

  const [org, setOrg] = useState(null);
  const [orgError, setOrgError] = useState(null);
  const [orgLoading, setOrgLoading] = useState(true);

  const [tab, setTab] = useState("Upcoming");
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  // Look up org by slug. Try a name-param GET first; if that misses,
  // fall back to the full list and slug-match locally.
  useEffect(() => {
    let cancelled = false;

    async function loadOrg() {
      if (!slug) return;
      setOrgLoading(true);
      setOrgError(null);

      try {
        const allRes = await axios.get(`${api}organizations/`, {
          headers: {
            "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
          },
        });
        const allOrgs = allRes?.data?.data || [];
        const match = allOrgs.find(
          (o) => slugifyOrgName(o?.name) === slug.toLowerCase()
        );

        if (cancelled) return;

        if (match) {
          setOrg(match);
          if (typeof document !== "undefined") {
            document.title = `${match.name} on Ummahfy`;
          }
        } else {
          setOrg(null);
          setOrgError("not_found");
        }
      } catch (err) {
        console.error("Failed to load organization:", err);
        if (!cancelled) {
          setOrg(null);
          setOrgError("load_failed");
        }
      } finally {
        if (!cancelled) setOrgLoading(false);
      }
    }

    loadOrg();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Pull events for this org once we know its name.
  useEffect(() => {
    let cancelled = false;

    async function loadEvents() {
      if (!org?.name) return;
      setEventsLoading(true);
      try {
        const response = await axios.post(
          `${api}events/org`,
          { organization: org.name },
          {
            headers: {
              "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
            },
          }
        );

        if (cancelled) return;

        if (response?.data?.status) {
          const past = response.data.data.past || [];
          const upcoming = response.data.data.upcoming || [];
          // Public viewers should only see approved, non-private events.
          setUpcomingEvents(
            upcoming.filter((e) => e.isApproved && !e.isPrivate)
          );
          setPastEvents(past.filter((e) => e.isApproved && !e.isPrivate));
        } else {
          setUpcomingEvents([]);
          setPastEvents([]);
        }
      } catch (err) {
        console.error("Failed to load org events:", err);
        if (!cancelled) {
          setUpcomingEvents([]);
          setPastEvents([]);
        }
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    }

    loadEvents();
    return () => {
      cancelled = true;
    };
  }, [org?.name]);

  // Only people who have a role on THIS organization (owner, co-owner,
  // per-org admin, or collaborator) see the Manage button. Site-wide
  // admin status alone is intentionally not enough — the public org page
  // should look the same to platform admins as to anyone else unless
  // they're tied to this specific org.
  const managesThisOrg = Array.isArray(managedOrganizations)
    ? managedOrganizations.some(
        (o) => o?.name && org?.name && o.name === org.name
      )
    : false;
  const canManage = managesThisOrg;

  // Switch the active organizer org (if needed) and send them to the
  // dashboard. This mirrors what OrganizationSelector does.
  async function handleManageClick() {
    if (!org?.name) return;
    try {
      if (managesThisOrg && selectedOrganization !== org.name) {
        const orgInfo = managedOrganizations.find((o) => o.name === org.name);
        setAppData((prev) => ({
          ...prev,
          selectedOrganization: org.name,
          selectedOrganizationInfo: orgInfo || prev.selectedOrganizationInfo,
        }));
        await axios.post(
          `${api}switchSelectedOrganization/`,
          { org: org.name },
          {
            withCredentials: true,
            headers: {
              "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
            },
          }
        );
      }
    } catch (err) {
      console.error("Failed to switch organization before managing:", err);
    } finally {
      navigate("/organization");
    }
  }

  function renderEventCard(info) {
    if (!info) return null;
    return (
      <Link
        key={info.event_id}
        to={`/event/${info.event_id}`}
        className="card-link"
      >
        <EventCard
          imgurl={info.img}
          event={info.name}
          masjid={info.organization}
          matchedOrg={
            org?.name &&
            info.organization &&
            org.name.toLowerCase() ===
              String(info.organization).toLowerCase()
              ? org
              : null
          }
          date={info.timeArray?.[0]}
          altdate={
            info.dateIndex === -1
              ? info.dates?.[0]
              : info.dates?.[info.dateIndex]
          }
          description={info.description}
          age={info.age_range}
          frequency={info.frequency}
          price={info.price}
          gender={info.gender}
          registered={info.registered}
          time={info.start_time}
          tags={info.tags}
        />
      </Link>
    );
  }

  if (orgLoading) {
    return (
      <>
        <div className="wrapper">
          <Navbar />
          <div className="settings-wrapper">
            <div style={{ padding: "60px 0", textAlign: "center" }}>
              <Spin size="large" />
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!org) {
    return (
      <>
        <div className="wrapper">
          <Navbar />
          <div className="settings-wrapper">
            <div style={{ padding: "60px 16px", textAlign: "center" }}>
              <h1 className="org-title" style={{ marginBottom: 8 }}>
                Organization not found
              </h1>
              <p style={{ color: "#bbb", marginBottom: 20 }}>
                {orgError === "load_failed"
                  ? "We couldn't load this organization. Please try again."
                  : "We couldn't find an organization at this URL."}
              </p>
              <Button onClick={() => navigate("/")}>Back to home</Button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const description = org.description || "";
  const location = org.location || "";

  return (
    <>
      <div className="wrapper">
        <Navbar />

        <div className="settings-wrapper">
          <div className="profile-card">
            {canManage && (
              <div
                className="profile-card-actions"
                style={{
                  position: "absolute",
                  top: "20px",
                  right: "20px",
                  display: "flex",
                  zIndex: 10,
                }}
              >
                <Button
                  icon={<SettingOutlined />}
                  className="org-settings-btn"
                  onClick={handleManageClick}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    width: "40px",
                    height: "40px",
                  }}
                  title="Manage organization"
                >
                  Manage
                </Button>
              </div>
            )}

            <div className="org-banner">
              {org.bannerPicture && (
                <img
                  src={org.bannerPicture}
                  alt={`${org.name} banner`}
                  className="org-banner-img"
                />
              )}
            </div>

            <div className="org-content">
              <div className="org-profile-pic-container">
                {org.profilePicture ? (
                  <img
                    src={org.profilePicture}
                    alt={`${org.name} logo`}
                    className="org-profile-img"
                  />
                ) : (
                  <Avatar className="org-profile-img" />
                )}
              </div>

              <div className="prof-title-edit">
                <h1 className="org-title">{org.name}</h1>
              </div>

              {location && (
                <div className="org-profile-address">
                  <p className="org-address">{location}</p>
                </div>
              )}

              {description ? (
                <div className="org-description">
                  <div dangerouslySetInnerHTML={{ __html: description }} />
                </div>
              ) : null}
            </div>
          </div>

          <div className="org-title-toggle">
            <h1 className="org-events-title">Events</h1>
            <ConfigProvider
              theme={{
                components: {
                  Segmented: {
                    trackBg: "rgba(255, 255, 255, 0.075)",
                    itemColor: "#ccc",
                    itemSelectedColor: "#fff",
                    itemHoverColor: "#fff",
                    borderRadius: 4,
                    borderRadiusLG: 4,
                    itemSelectedBg: "rgba(255, 255, 255, 0.075)",
                    controlHeight: 36,
                  },
                },
              }}
            >
              <Segmented
                size="large"
                options={["Upcoming", "Past"]}
                value={tab}
                onChange={setTab}
              />
            </ConfigProvider>
          </div>

          {eventsLoading ? (
            <div style={{ padding: "40px 0", textAlign: "center" }}>
              <Spin />
            </div>
          ) : tab === "Upcoming" ? (
            upcomingEvents.length > 0 ? (
              upcomingEvents.map(renderEventCard)
            ) : (
              <div className="no-events-div">
                <NoEvents className="prof-no-events" />
              </div>
            )
          ) : pastEvents.length > 0 ? (
            pastEvents.map(renderEventCard)
          ) : (
            <div className="no-events-div">
              <NoEvents className="prof-no-events" />
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

export default OrganizationProfile;
